import { useCallback, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import clsx from 'clsx';
import { Icon } from '@/components/Icon';
import { getApp } from '@/apps/registry';
import { DropdownMenu } from '@/shell/Menus/DropdownMenu';
import { separator } from '@/shell/Menus/types';
import type { MenuItem } from '@/shell/Menus/types';
import { showContextMenu } from '@/shell/ContextMenu/ContextMenu';
import { useWindowStore } from '@/wm/store';
import type { WindowState } from '@/wm/types';
import css from './Taskbar.module.css';

export interface TaskGroupButtonProps {
  appId: string;
  windows: readonly WindowState[];
}

/**
 * One button standing for several windows of the same program, the way XP
 * folded them once the taskbar filled up. Clicking it lists them; choosing one
 * does what its own button would have done.
 */
export function TaskGroupButton({ appId, windows }: TaskGroupButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const app = getApp(appId);
  const name = app?.name ?? windows[0]?.title ?? 'Windows';
  const icon = windows[0]?.icon || app?.icon || '';
  // A group draws as pressed when the window on top belongs to it.
  const active = windows.some((w) => w.focused && !w.minimized);

  const close = useCallback(() => setOpen(false), []);

  const items: MenuItem[] = [
    ...windows.map((win) => ({
      label: win.title,
      icon: win.icon,
      onSelect: () => useWindowStore.getState().toggleFromTaskbar(win.id),
    })),
    separator,
    {
      label: '&Minimize Group',
      onSelect: () => {
        const store = useWindowStore.getState();
        for (const win of windows) if (!win.minimized) store.minimize(win.id);
      },
    },
    {
      label: '&Close Group',
      onSelect: () => {
        const store = useWindowStore.getState();
        for (const win of windows) store.close(win.id);
      },
    },
  ];

  /*
   * Toggling on pointerdown rather than click, and stopping the event there.
   * `DropdownMenu` dismisses itself on a document pointerdown, so a click that
   * both closed the popup and re-opened it would leave the button looking
   * inert. Stopping propagation keeps that handler from ever seeing this one.
   */
  function togglePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (event.button !== 0) return;
    event.stopPropagation();
    setOpen((v) => !v);
  }

  function handleContextMenu(event: ReactMouseEvent<HTMLButtonElement>) {
    setOpen(false);
    showContextMenu(event, items);
  }

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={clsx(css.task, css.taskGroup, active && css.taskActive)}
        title={`${windows.length} ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onPointerDown={togglePointerDown}
        onContextMenu={handleContextMenu}
      >
        <Icon src={icon} size={16} />
        <span className={css.taskCount}>{windows.length}</span>
        <span className={css.taskLabel}>{name}</span>
        <span className={css.taskChevron} aria-hidden="true" />
      </button>

      {open && rect ? (
        /* Anchored to the button's top edge: the taskbar is at the bottom of
           the screen, so the list has nowhere to go but up, and DropdownMenu
           flips it there itself once it knows its own height. */
        <DropdownMenu
          items={items}
          x={Math.round(rect.left)}
          y={Math.round(rect.top)}
          onClose={close}
        />
      ) : null}
    </>
  );
}
